/**
 * Simple per-step components for the Content Wizard.
 * Keeps ContentCreatePage.js lean by extracting the low-complexity steps 0-2.
 * Steps 3-6 remain inline due to tighter coupling with wizard state (media thumbs, AI generation, preview, schedule).
 */
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Calendar as CalendarComp } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Sparkles, Loader2, Save, Check, CalendarIcon, Clock, Palette } from "lucide-react";
import { useEffect, useState } from "react";
import axios from "axios";
import { Switch } from "@/components/ui/switch";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const GOALS = [
  { value: "reichweite", labelKey: "content.goal_reach", fallback: "Reichweite" },
  { value: "markenbekanntheit", labelKey: "content.goal_awareness", fallback: "Markenbekanntheit" },
  { value: "interaktion", labelKey: "content.goal_engagement", fallback: "Interaktionen" },
  { value: "leads", labelKey: "content.goal_leads", fallback: "Leads" },
  { value: "verkauf", labelKey: "content.goal_sales", fallback: "Verkäufe" },
  { value: "website_besuche", labelKey: "content.goal_traffic", fallback: "Website-Besuche" },
  { value: "termine", labelKey: "content.goal_bookings", fallback: "Terminbuchungen" },
  { value: "event", labelKey: "content.goal_event", fallback: "Event-Bewerbung" },
  { value: "saisonal", labelKey: "content.goal_seasonal", fallback: "Saisonale Aktion" },
];

const FORMATS = [
  { value: "Instagram Post",  label: "Instagram Post",  platform: "instagram", type: "post" },
  { value: "Instagram Reel",  label: "Instagram Reel",  platform: "instagram", type: "reel" },
  { value: "Instagram Story", label: "Instagram Story", platform: "instagram", type: "story" },
  { value: "Facebook Post",   label: "Facebook Post",   platform: "facebook",  type: "post" },
  { value: "Facebook Reel",   label: "Facebook Reel",   platform: "facebook",  type: "reel" },
  { value: "Facebook Story",  label: "Facebook Story",  platform: "facebook",  type: "story" },
  { value: "TikTok Video",    label: "TikTok Video",    platform: "tiktok",    type: "reel" },
  { value: "TikTok Story",    label: "TikTok Story",    platform: "tiktok",    type: "story" },
  { value: "LinkedIn Post",   label: "LinkedIn Post",   platform: "linkedin",  type: "post" },
  { value: "LinkedIn Video",  label: "LinkedIn Video",  platform: "linkedin",  type: "reel" },
  { value: "Pinterest Pin",   label: "Pinterest Pin",   platform: "pinterest", type: "post" },
];

const btnBase = "w-full text-left p-4 rounded-lg border transition-all";
const btnActive = "border-[#2563EB] bg-[#2563EB]/5";
const btnIdle = "border-[var(--studio-border)] hover:border-gray-300";

export function StepCompany({ companies, companyId, setCompanyId, t }) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader><CardTitle className="font-heading">{t("content.select_company")}</CardTitle></CardHeader>
      <CardContent className="space-y-3">
        {companies.map((c) => (
          <button
            key={c.id}
            onClick={() => setCompanyId(c.id)}
            data-testid={`select-company-${c.id}`}
            className={`${btnBase} ${companyId === c.id ? btnActive : btnIdle}`}
          >
            <p className="font-medium font-heading">{c.name}</p>
            <p className="text-xs text-[var(--studio-text-muted)]">{c.industry}</p>
          </button>
        ))}
        {companies.length === 0 && (
          <p className="text-sm text-[var(--studio-text-muted)]">
            Keine Firmen vorhanden. Erstelle zuerst eine Firma.
          </p>
        )}
      </CardContent>
    </Card>
  );
}

export function StepGoal({ goal, setGoal, t }) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader><CardTitle className="font-heading">{t("content.goal_title")}</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        {GOALS.map((g) => (
          <button
            key={g.value}
            onClick={() => setGoal(g.value)}
            data-testid={`select-goal-${g.value}`}
            className={`p-4 rounded-lg border text-left transition-all ${goal === g.value ? btnActive : btnIdle}`}
          >
            <p className="font-medium">{g.fallback}</p>
          </button>
        ))}
      </CardContent>
    </Card>
  );
}

export function StepFormat({ formatType, setFormatType, t, activePlatforms, connectionsOnly }) {
  // Show only formats for platforms that are actually connected (OAuth); fallback: company.platforms; empty => all.
  const visible = (activePlatforms && activePlatforms.length > 0)
    ? FORMATS.filter((f) => activePlatforms.includes(f.platform))
    : FORMATS;
  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader><CardTitle className="font-heading">{t("content.format_title")}</CardTitle></CardHeader>
      <CardContent className="grid grid-cols-2 gap-3">
        {connectionsOnly && (
          <p className="col-span-2 text-xs text-[var(--studio-text-muted)]" data-testid="format-connected-hint">
            Es werden nur Formate der verbundenen Konten angezeigt. Weitere Plattformen kannst du auf der Social-Media-Seite verbinden.
          </p>
        )}
        {visible.length === 0 ? (
          <p className="col-span-2 text-sm text-amber-700 p-3 rounded-md bg-amber-50 border border-amber-200">
            Keine Plattformen fuer diese Firma aktiviert. Bitte im Menue „Firmen" mindestens eine Plattform auswaehlen.
          </p>
        ) : (
          visible.map((f) => (
            <button
              key={f.value}
              onClick={() => setFormatType(f.value)}
              data-testid={`select-format-${f.value}`}
              className={`p-4 rounded-lg border text-left transition-all ${formatType === f.value ? btnActive : btnIdle}`}
            >
              <p className="font-medium">{f.label}</p>
              <p className="text-xs text-[var(--studio-text-muted)] mt-1 capitalize">{f.platform} - {f.type}</p>
            </button>
          ))
        )}
      </CardContent>
    </Card>
  );
}

export { GOALS as goals, FORMATS as formats };

export function StepGenerate({
  companies, companyId, goal, formatType, selectedMedia,
  templates, selectedTemplate, setSelectedTemplate,
  campaigns, selectedCampaign, setSelectedCampaign,
  userBrief, setUserBrief,
  generating, onGenerate, t,
}) {
  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader><CardTitle className="font-heading">{t("content.generate_title")}</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        <div className="text-center py-2">
          <p className="text-sm text-[var(--studio-text-muted)] mb-1">Zusammenfassung:</p>
          <p className="font-medium">
            {companies.find((c) => c.id === companyId)?.name} &middot; {goal} &middot; {formatType}
          </p>
          {selectedMedia.length > 0 && (
            <p className="text-xs text-[var(--studio-text-muted)]">{selectedMedia.length} Medien ausgewaehlt</p>
          )}
        </div>

        <div className="p-4 rounded-lg bg-[var(--studio-bg)] space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-xs font-medium text-[var(--studio-text)]">
              📝 Kontext / Stichwoerter zum Bild <span className="text-[var(--studio-text-muted)] font-normal">(optional)</span>
            </Label>
            <span className="text-[10px] text-[var(--studio-text-muted)]">{(userBrief || "").length}/500</span>
          </div>
          <textarea
            value={userBrief || ""}
            onChange={(e) => setUserBrief(e.target.value.slice(0, 500))}
            placeholder='z.B. "Stadtfest Etzikon 2018 war ein voller Erfolg" oder "Neue Sommer-Kollektion, ab Freitag im Shop, Rabatt 20%"'
            rows={3}
            className="w-full px-3 py-2 rounded-md border border-[var(--studio-border)] bg-white text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[#2563EB]/30"
            data-testid="user-brief-input"
          />
          <p className="text-[10px] text-[var(--studio-text-muted)]">
            Gib der KI Kontext: was zeigt das Bild, was ist die Story, was soll erwaehnt werden? Je konkreter, desto besser die Caption.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 p-4 rounded-lg bg-[var(--studio-bg)]">
          <div>
            <Label className="text-xs text-[var(--studio-text-muted)]">{t("content.template_select")}</Label>
            <Select value={selectedTemplate} onValueChange={setSelectedTemplate}>
              <SelectTrigger className="mt-1 bg-white" data-testid="template-select">
                <SelectValue placeholder="Kein Template" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("content.no_template")}</SelectItem>
                {templates.map((tpl) => (
                  <SelectItem key={tpl.id} value={tpl.id}>{tpl.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-xs text-[var(--studio-text-muted)]">{t("content.campaign_select")}</Label>
            <Select value={selectedCampaign} onValueChange={setSelectedCampaign}>
              <SelectTrigger className="mt-1 bg-white" data-testid="campaign-select">
                <SelectValue placeholder="Keine Kampagne" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t("content.no_campaign")}</SelectItem>
                {campaigns.map((c) => (
                  <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="text-center">
          <Button
            onClick={onGenerate}
            disabled={generating}
            data-testid="generate-content-btn"
            className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white px-8 h-12"
          >
            {generating ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
            {generating ? t("content.generating") : t("content.generate_btn")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

export function StepSave({
  scheduleDate, setScheduleDate, scheduleTime, setScheduleTime,
  saving, onSave, t, companyId, formatType,
  applyBrandKit, setApplyBrandKit, brandKitInfo, selectedMedia, hasVideoSelected,
  businessUnits = [], businessUnitId = "none", setBusinessUnitId,
  ctaLinkId = "auto", setCtaLinkId = () => {},
}) {
  const timeOptions = [];
  for (let h = 0; h < 24; h++) {
    for (let m = 0; m < 60; m += 15) {
      timeOptions.push(`${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`);
    }
  }

  const dateObj = scheduleDate ? new Date(scheduleDate) : null;
  const dateLabel = dateObj
    ? dateObj.toLocaleDateString("de-CH", { weekday: "short", day: "2-digit", month: "short", year: "numeric" })
    : "Datum waehlen";

  // Best-Time Suggestions
  const [bestTimes, setBestTimes] = useState([]);
  const [btLoading, setBtLoading] = useState(false);
  const [btSource, setBtSource] = useState("");
  const platform = /facebook/i.test(formatType || "") ? "facebook"
    : /tiktok/i.test(formatType || "") ? "tiktok"
    : /linkedin/i.test(formatType || "") ? "linkedin"
    : "instagram";
  const fmtKind = /story/i.test(formatType || "") ? "story" : /reel/i.test(formatType || "") ? "reel" : "post";

  useEffect(() => {
    if (!companyId) return;
    setBtLoading(true);
    axios.get(`${API}/ai/best-time`, {
      params: { company_id: companyId, format: fmtKind, platform },
      withCredentials: true,
    })
      .then((r) => {
        setBestTimes(r.data?.suggestions || []);
        setBtSource(r.data?.suggestions?.[0]?.source || "");
      })
      .catch(() => setBestTimes([]))
      .finally(() => setBtLoading(false));
  }, [companyId, platform, fmtKind]);

  const applySlot = (slot) => {
    const dt = new Date(slot.next_iso);
    const iso = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
    setScheduleDate(iso);
    setScheduleTime(`${String(slot.hour).padStart(2, "0")}:00`);
  };

  // CTA-Tracking-Links der Firma
  const [ctaLinks, setCtaLinks] = useState([]);
  useEffect(() => {
    if (!companyId) return;
    axios.get(`${API}/tracking-links`, { params: { company_id: companyId }, withCredentials: true })
      .then((r) => setCtaLinks(r.data?.items || []))
      .catch(() => setCtaLinks([]));
  }, [companyId]);
  const CTA_CHANNELS = {
    instagram: ["Instagram", "Instagram Bio", "Instagram Story"],
    facebook: ["Facebook"], linkedin: ["LinkedIn"], tiktok: ["TikTok"],
  };
  const autoCta = ctaLinks.find((l) => (CTA_CHANNELS[platform] || []).includes(l.channel));

  return (
    <Card className="border-[var(--studio-border)] shadow-none animate-fade-in">
      <CardHeader><CardTitle className="font-heading">Speichern & Planen</CardTitle></CardHeader>
      <CardContent className="space-y-4">
        {/* Best Time Suggestions */}
        <div className="bg-[var(--studio-bg)] rounded-lg p-3 space-y-2" data-testid="best-time-suggestions">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-[#2563EB]" />
            <p className="text-sm font-medium">Beste Posting-Zeiten</p>
            {btSource === "industry_default" && (
              <span className="text-[10px] bg-amber-100 text-amber-800 px-1.5 py-0.5 rounded">Branchen-Standard</span>
            )}
            {btSource === "history" && (
              <span className="text-[10px] bg-green-100 text-green-800 px-1.5 py-0.5 rounded">Basierend auf Historie</span>
            )}
          </div>
          {btLoading ? (
            <Loader2 className="w-4 h-4 animate-spin text-[var(--studio-text-muted)]" />
          ) : bestTimes.length === 0 ? (
            <p className="text-xs text-[var(--studio-text-muted)]">Keine Vorschlaege verfuegbar.</p>
          ) : (
            <div className="flex flex-wrap gap-2">
              {bestTimes.slice(0, 5).map((s, i) => (
                <button
                  key={i}
                  onClick={() => applySlot(s)}
                  data-testid={`best-time-chip-${i}`}
                  className="text-xs px-3 py-1.5 rounded-full border border-[var(--studio-border)] bg-white hover:border-[#2563EB] hover:bg-[#2563EB]/5 transition-all"
                >
                  {s.weekday_label} {String(s.hour).padStart(2, "0")}:00
                </button>
              ))}
            </div>
          )}
        </div>

        {/* CTA-Tracking-Link */}
        <div className="bg-[var(--studio-bg)] rounded-lg p-3 space-y-2" data-testid="cta-link-section">
          <Label className="text-sm font-medium">🔗 Link im Post (Tracking-Link)</Label>
          <Select value={ctaLinkId} onValueChange={setCtaLinkId}>
            <SelectTrigger className="mt-1" data-testid="cta-link-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="auto">
                Automatisch{autoCta ? ` — ${(autoCta.pretty_url || autoCta.short_url || "").replace(/^https?:\/\//, "")}` : " (kein passender Kanal-Link vorhanden)"}
              </SelectItem>
              <SelectItem value="none">Kein Link</SelectItem>
              {ctaLinks.map((l) => (
                <SelectItem key={l.id} value={l.id}>
                  {l.name} — {(l.pretty_url || l.short_url || "").replace(/^https?:\/\//, "")}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-[11px] text-[var(--studio-text-muted)]">
            {["facebook", "linkedin"].includes(platform)
              ? "Der Link wird klickbar am Ende der Caption eingefügt. Jeder Klick wird gemessen."
              : "Instagram/TikTok erlauben keine Klick-Links in Captions — der Post erwähnt «Link in Bio». Lege den gewählten Link in die Bio des Kontos."}
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4">
          <div>
            <Label className="text-xs text-[var(--studio-text-muted)]">Datum (optional)</Label>
            <Popover>
              <PopoverTrigger asChild>
                <Button
                  variant="outline"
                  className="mt-1 w-full justify-start font-normal"
                  data-testid="schedule-date"
                >
                  <CalendarIcon className="w-4 h-4 mr-2 text-[var(--studio-text-muted)]" />
                  <span className={dateObj ? "" : "text-[var(--studio-text-muted)]"}>{dateLabel}</span>
                </Button>
              </PopoverTrigger>
              <PopoverContent className="w-auto p-0" align="start">
                <CalendarComp
                  mode="single"
                  selected={dateObj}
                  onSelect={(d) => {
                    if (!d) { setScheduleDate(""); return; }
                    const iso = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
                    setScheduleDate(iso);
                  }}
                  initialFocus
                />
                {dateObj && (
                  <div className="p-2 border-t flex justify-end">
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => setScheduleDate("")}
                      data-testid="schedule-date-clear"
                      className="text-xs h-7"
                    >
                      Zuruecksetzen
                    </Button>
                  </div>
                )}
              </PopoverContent>
            </Popover>
          </div>
          <div>
            <Label className="text-xs text-[var(--studio-text-muted)]">Uhrzeit</Label>
            <Select value={scheduleTime} onValueChange={setScheduleTime}>
              <SelectTrigger className="mt-1" data-testid="schedule-time">
                <SelectValue placeholder="Uhrzeit" />
              </SelectTrigger>
              <SelectContent className="max-h-72">
                {timeOptions.map((val) => (
                  <SelectItem key={val} value={val}>{val}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>
        {businessUnits.length > 0 && (
          <div>
            <Label className="text-xs text-[var(--studio-text-muted)]">Standort / Geschaeftsbereich (optional)</Label>
            <Select value={businessUnitId} onValueChange={setBusinessUnitId}>
              <SelectTrigger className="mt-1" data-testid="business-unit-select">
                <SelectValue placeholder="Kein Bereich" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Kein Bereich</SelectItem>
                {businessUnits.map((u) => (
                  <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <Separator />
        {brandKitInfo && (
          <BrandKitToggle
            hasVideoSelected={hasVideoSelected}
            applyBrandKit={applyBrandKit}
            setApplyBrandKit={setApplyBrandKit}
            brandKitInfo={brandKitInfo}
            formatType={formatType}
            selectedMedia={selectedMedia}
            companyId={companyId}
          />
        )}
        <div className="flex gap-3">
          <Button
            onClick={() => onSave("draft")}
            disabled={saving}
            variant="outline"
            className="flex-1"
            data-testid="save-draft-btn"
          >
            <Save className="w-4 h-4 mr-2" /> {t("content.save_draft")}
          </Button>
          <Button
            onClick={() => onSave("review")}
            disabled={saving}
            className="flex-1 bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
            data-testid="submit-review-btn"
          >
            <Check className="w-4 h-4 mr-2" /> {t("content.submit_review")}
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function BrandKitToggle() {
  // Brand-Overlay komplett deaktiviert — es werden immer Original-Bilder gepostet
  return null;
}


